const API_URL = (process.env.REACT_APP_API_URL || "/api").replace(/\/$/, "");
const SESSION_KEY = "facteur_session";

const getSession = () => {
  try { return JSON.parse(localStorage.getItem(SESSION_KEY) || "null"); } catch { return null; }
};

async function request(path, options = {}) {
  const session = getSession();
  const headers = { ...(options.body instanceof Blob ? { "content-type": "application/pdf" } : { "content-type": "application/json" }), ...(options.headers || {}) };
  if (session?.access_token) headers.authorization = `Bearer ${session.access_token}`;
  const response = await fetch(`${API_URL}${path}`, { ...options, headers });
  const responseText = await response.text();
  let body = {};
  try { body = responseText ? JSON.parse(responseText) : {}; } catch { /* Keep the plain response text for the error below. */ }
  if (!response.ok) throw new Error(body.error?.message || responseText || "Erreur de communication avec le serveur.");
  return body;
}

class QueryBuilder {
  constructor(table) {
    this.table = table;
    this.operation = "select";
    this.columns = "*";
    this.filters = [];
    this.ordering = null;
    this.rows = null;
    this.values = null;
    this.isSingle = false;
  }

  select(columns = "*") {
    this.columns = columns;
    return this;
  }

  eq(column, value) {
    this.filters.push({ column, operator: "eq", value });
    return this;
  }

  like(column, value) {
    this.filters.push({ column, operator: "like", value });
    return this;
  }

  order(column, options = {}) {
    this.ordering = { column, ascending: options.ascending !== false };
    return this;
  }

  single() {
    this.isSingle = true;
    return this;
  }

  insert(rows) {
    this.operation = "insert";
    this.rows = rows;
    return this;
  }

  update(values) {
    this.operation = "update";
    this.values = values;
    return this;
  }

  delete() {
    this.operation = "delete";
    return this;
  }

  async execute() {
    const result = await request("/query", {
      method: "POST",
      body: JSON.stringify({
        table: this.table,
        operation: this.operation,
        columns: this.columns,
        filters: this.filters,
        order: this.ordering,
        rows: this.rows,
        values: this.values
      })
    });
    if (this.isSingle) return { ...result, data: result.data?.[0] || null };
    return result;
  }

  then(resolve, reject) { return this.execute().then(resolve, reject); }
  catch(reject) { return this.execute().catch(reject); }
}

const auth = {
  async signInWithPassword(credentials) {
    try {
      const result = await request("/auth/signin", { method: "POST", body: JSON.stringify(credentials) });
      localStorage.setItem(SESSION_KEY, JSON.stringify(result.session));
      return { data: result, error: null };
    } catch (error) { return { data: { user: null, session: null }, error }; }
  },
  async signUp({ email, password, options }) {
    try {
      const result = await request("/auth/signup", { method: "POST", body: JSON.stringify({ email, password, options }) });
      return { data: result, error: null };
    } catch (error) { return { data: { user: null, session: null }, error }; }
  },
  async getSession() {
    try {
      const result = await request("/auth/session", { method: "GET" });
      if (result.session) localStorage.setItem(SESSION_KEY, JSON.stringify(result.session));
      return { data: result, error: null };
    } catch { return { data: { session: null }, error: null }; }
  },
  async getUser() {
    try {
      const result = await request("/auth/user", { method: "GET" });
      return { data: result, error: null };
    } catch { return { data: { user: null }, error: null }; }
  },
  async getProfile() {
    try {
      const result = await request("/auth/profile", { method: "GET" });
      return { data: result, error: null };
    } catch (error) { return { data: { profile: null }, error }; }
  },
  async updateUser({ user_id, email, password }) {
    try {
      const payload = {};
      if (user_id) payload.user_id = user_id;
      if (email) payload.email = email;
      if (password) payload.password = password;
      const result = await request("/auth/update", { method: "POST", body: JSON.stringify(payload) });
      if (result.session) localStorage.setItem(SESSION_KEY, JSON.stringify(result.session));
      return { data: result, error: null };
    } catch (error) { return { data: null, error }; }
  },
  async promote() {
    try {
      const result = await request("/auth/promote", { method: "POST", body: "{}" });
      if (result.session) localStorage.setItem(SESSION_KEY, JSON.stringify(result.session));
      return { data: result, error: null };
    } catch (error) { return { data: null, error }; }
  },
  async signOut() {
    localStorage.removeItem(SESSION_KEY);
    return { error: null };
  }
};

const storage = {
  from() {
    return {
      async upload(path, file) {
        try {
          const result = await request("/storage/upload", { method: "POST", body: file, headers: { "x-file-name": path } });
          return { data: { path: result.key }, error: null };
        } catch (error) { return { data: null, error }; }
      },
      getPublicUrl(path) {
        const token = getSession()?.access_token || "";
        return { data: { publicUrl: `${API_URL}/storage/file/${path}?token=${encodeURIComponent(token)}` } };
      }
    };
  }
};

export const supabase = {
  from: table => new QueryBuilder(table),
  auth,
  storage
};
